import React, { useEffect, useRef, useState } from "react";
import { handleMultiEmotion } from "../../../StudentData/handlers/multiEmotionHandler";
import {
  emotionEmojiImgMapping,
  emotionGroups,
} from "../../utils/emotionColorEmoji";
import { X, Info } from "lucide-react";
import { challengingEmotions } from "../../utils";

const EmotionDropdown = ({
  state,
  updateState,
  selectedEmotions,
  clearEmotion,
}) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleClick = (emotion) => {
    let allEmotions = [...selectedEmotions];
    const exists = allEmotions.includes(emotion);
    allEmotions = exists
      ? allEmotions.filter((e) => e !== emotion)
      : [...allEmotions, emotion];

    const status = allEmotions.length > 0;
    updateState({ ...state, multiEmotion: { status, emotions: allEmotions } });
    updateState(handleMultiEmotion(state, allEmotions));
  };

  const handleGroupToggle = (emotions) => {
    const allSelected = emotions.every((e) => selectedEmotions.includes(e));
    const newSelection = allSelected
      ? selectedEmotions.filter((e) => !emotions.includes(e))
      : [...new Set([...selectedEmotions, ...emotions])];
    const status = newSelection.length > 0;
    updateState({ ...state, multiEmotion: { status, emotions: newSelection } });
    updateState(handleMultiEmotion(state, newSelection));
  };

  const getColorClass = (label) => {
    if (label.includes("Blue")) return "#5B9CD6";
    if (label.includes("Green")) return "#4CC68D";
    if (label.includes("Purple")) return "#BD92E4";
    return;
  };

  const allEmotionList = emotionGroups.flatMap((group) => group.emotions);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {!open ? (
        <div
          className="justify-between rounded p-1.5 border border-[#DDDDDD] w-full h-full flex items-center cursor-pointer bg-white flex-wrap gap-2"
          onClick={() => setOpen(true)}
        >
          <div className="flex flex-wrap items-center gap-2 flex-1">
            {selectedEmotions.length === 9 ? (
              <span className="flex items-center gap-2 bg-blue-100 text-[#2763D4] text-[14px] px-2 py-1 rounded-full">
                All emotions
                <button className="text-[#2763D4]">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ) : (
              <>
                {selectedEmotions.slice(0, 2).map((emotion) => (
                  <span
                    key={emotion}
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-2 bg-blue-100 text-[#2763D4] text-[14px] px-2 py-1 rounded-full capitalize"
                  >
                    {emotion}
                    <button
                      onClick={(e) => clearEmotion(e, emotion)}
                      className="text-[#2763D4]"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {selectedEmotions.length > 2 && (
                  <span className="text-[14px] text-400">
                    +{selectedEmotions.length - 2} more
                  </span>
                )}
              </>
            )}
          </div>
          <svg
            className="w-3 h-3 text-gray-600 cursor-pointer mr-2"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 10 6"
            fill="currentColor"
          >
            <path d="M0 0l5 6 5-6H0z" />
          </svg>
        </div>
      ) : (
        <div className="w-full bg-white border border-[#DDDDDD] rounded p-3">
          <div className="text-[14px] mb-2 flex justify-between items-center">
            <span>Select Emotion</span>
            <svg
              className="w-3 h-3 text-gray-600 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(!open);
              }}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 10 6"
              fill="currentColor"
            >
              <path d="M0 0l5 6 5-6H0z" />
            </svg>
          </div>

          {/* ALL EMOTIONS */}
          <div className="mb-2">
            <div
              onClick={() => {
                const isAllSelected =
                  selectedEmotions.length === allEmotionList.length;
                const next = isAllSelected ? [] : allEmotionList;
                updateState({
                  ...state,
                  multiEmotion: {
                    status: next.length > 0,
                    emotions: next,
                  },
                });
                updateState(handleMultiEmotion(state, next));
              }}
              className="flex items-center gap-2 text-[14px] cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedEmotions.length === allEmotionList.length}
                readOnly
                className="appearance-none w-4 h-4 border border-1 rounded checked:border-transparent"
                style={{
                  backgroundColor:
                    selectedEmotions.length === allEmotionList.length
                      ? "#4CC68D"
                      : "transparent",
                }}
              />
              All Emotions
            </div>
          </div>

          {/* CHALLENGING EMOTIONS */}
          <div className="mb-2">
            <div className="flex items-center justify-between text-[14px]">
              <div
                onClick={() => {
                  const isSelected =
                    selectedEmotions.length === challengingEmotions.length &&
                    challengingEmotions.every((e) =>
                      selectedEmotions.includes(e)
                    );

                  const next = isSelected ? [] : challengingEmotions;

                  updateState({
                    ...state,
                    multiEmotion: {
                      status: next.length > 0,
                      emotions: next,
                    },
                  });
                  updateState(handleMultiEmotion(state, next));
                }}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={
                    selectedEmotions.length === challengingEmotions.length &&
                    challengingEmotions.every((e) =>
                      selectedEmotions.includes(e)
                    )
                  }
                  readOnly
                  className="appearance-none w-4 h-4 border border-1 rounded checked:border-transparent"
                  style={{
                    backgroundColor:
                      selectedEmotions.length === challengingEmotions.length &&
                      challengingEmotions.every((e) =>
                        selectedEmotions.includes(e)
                      )
                        ? "#F87171"
                        : "transparent",
                  }}
                />
                Challenging Emotions
              </div>
              <div className="group cursor-pointer">
                <Info className="w-3 h-3 text-gray-500" />
                <div className="absolute top-15 right-0 w-full h-20 bg-[#FCFAFE] bottom-full hidden group-hover:block text-[14px] p-2 shadow-md rounded z-10">
                  See students who felt any challenging emotions including sad,
                  lonely, stressed, angry, or scared.
                </div>
              </div>
            </div>
          </div>

          {emotionGroups.map(({ label, emotions }) => {
            const allSelected = emotions.every((e) =>
              selectedEmotions.includes(e)
            );
            return (
              <div key={label} className="mb-2">
                <div className="flex items-center justify-between capitalize text-[14px]">
                  <div
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={() => handleGroupToggle(emotions)}
                  >
                    <input
                      type="checkbox"
                      checked={allSelected}
                      readOnly
                      style={{
                        backgroundColor: allSelected
                          ? getColorClass(label)
                          : "transparent",
                      }}
                      className="appearance-none w-4 h-4 border border-1 rounded checked:border-transparent"
                    />
                    {label}
                  </div>
                </div>
                <ul className="grid grid-cols-1 gap-1 mt-1">
                  {emotions.map((emotion) => (
                    <li
                      key={emotion}
                      onClick={() => handleClick(emotion)}
                      className="flex items-center space-x-2 px-2 py-1 rounded hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedEmotions.includes(emotion)}
                        readOnly
                        style={{
                          backgroundColor: selectedEmotions.includes(emotion)
                            ? getColorClass(label)
                            : "transparent",
                        }}
                        className="appearance-none w-4 h-4 border border-1 rounded checked:border-transparent"
                      />
                      <img
                        src={emotionEmojiImgMapping[emotion]}
                        alt={emotion}
                        className="w-4 h-4"
                      />
                      <span className="text-[14px] capitalize">{emotion}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EmotionDropdown;
